import {
  getAccessToken,
  setAccessToken,
  setRefreshToken,
  terminateSessionAndRedirect,
} from "@/lib/api-client";
import { clearTokenExpiry, getRemainingAccessSec, recordTokenExpiry } from "@/lib/auth-session-expiry";
import {
  decodeAccessTokenUserId,
  publishRefreshResult,
  refreshWithBackoff,
  runWithCrossTabLock,
  subscribeRefreshResults,
} from "@/lib/auth-refresh-core";
import { TOKEN_REFRESHED_EVENT } from "@/lib/ws-events";

export const ACCESS_REFRESH_LEAD_SEC = 90;
export const ACCESS_REFRESH_ON_VISIBLE_SEC = 120;

const PROACTIVE_RETRY_DELAY_MS = 5_000;
const BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : "/api/v1";

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;
let proactiveTimerId: ReturnType<typeof setTimeout> | null = null;
let visibilityListenerAttached = false;
let coordinatorActive = false;
let skipAutoRedirectOnFailure = false;
let unsubscribeBroadcast: (() => void) | null = null;
let expectedUserId: string | null = null;
let legacyRefreshToken: string | null = null;
let onTokenRefreshed:
  | ((accessToken: string, refreshToken: string | null, expiresIn: number) => void)
  | null = null;

export function setCoordinatorOnTokenRefreshed(
  cb: ((accessToken: string, refreshToken: string | null, expiresIn: number) => void) | null,
): void {
  onTokenRefreshed = cb;
}

export function setCoordinatorSkipAutoRedirect(skip: boolean): void {
  skipAutoRedirectOnFailure = skip;
}

/**
 * Identity this tab believes it is signed in as. The customer app and the
 * management app share one origin, so a refresh could come back carrying a
 * different user; adopting it silently would hand one app the other's
 * privileges. When the minted token's subject does not match, we refuse it.
 */
export function setCoordinatorExpectedUserId(userId: string | null): void {
  expectedUserId = userId;
}

function isForeignSubject(accessToken: string): boolean {
  if (!expectedUserId) return false;
  const subject = decodeAccessTokenUserId(accessToken);
  return subject != null && subject !== expectedUserId;
}

/**
 * Hand over a refresh token found in web storage from a session created before
 * the cookie became authoritative. It is presented once, only matters when the
 * browser has no refresh cookie yet, and is dropped after the first refresh —
 * without it, upgrading would sign those sessions out.
 */
export function setCoordinatorLegacyRefreshToken(token: string | null): void {
  legacyRefreshToken = token;
}

export type RefreshAccessTokenOptions = {
  skipAutoRedirect?: boolean;
  /** Proactive path: one retry after 5s before terminal logout. */
  proactive?: boolean;
};

function applyRefreshSuccess(
  accessToken: string,
  expiresIn: number,
): void {
  setAccessToken(accessToken);
  // The refresh token itself is httpOnly-cookie only (API-SPECIFICATION §1.2);
  // nothing token-shaped is kept in JavaScript-reachable storage any more.
  setRefreshToken(null);
  recordTokenExpiry(expiresIn);
  if (onTokenRefreshed) onTokenRefreshed(accessToken, null, expiresIn);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: { accessToken } }),
    );
  }
}

/** Adopt a token another tab just minted (same app, same user). */
function adoptBroadcastToken(accessToken: string, expiresIn: number): void {
  if (isForeignSubject(accessToken)) return;
  setAccessToken(accessToken);
  recordTokenExpiry(expiresIn);
  if (onTokenRefreshed) onTokenRefreshed(accessToken, null, expiresIn);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: { accessToken } }),
    );
  }
  if (coordinatorActive) scheduleProactiveRefresh();
}

export async function refreshAccessToken(
  options: RefreshAccessTokenOptions = {},
): Promise<boolean> {
  const skipRedirect = options.skipAutoRedirect ?? skipAutoRedirectOnFailure;

  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    const outcome = await runWithCrossTabLock(async () => {
      // Another tab may have refreshed while we queued on the lock — adopt its
      // work instead of burning a second rotation.
      const remaining = getRemainingAccessSec();
      if (remaining != null && remaining > ACCESS_REFRESH_LEAD_SEC) {
        return { kind: "already-fresh" as const };
      }
      // Single shot: the legacy token is consumed by the first attempt whatever
      // the outcome. Re-sending it would replay an already-rotated token, which
      // the server reads as reuse.
      const legacyForThisAttempt = legacyRefreshToken;
      legacyRefreshToken = null;
      return refreshWithBackoff(BASE_URL, "web", {
        maxAttempts: options.proactive ? undefined : 2,
        legacyRefreshToken: legacyForThisAttempt,
      });
    });

    if (outcome.kind === "already-fresh") {
      if (coordinatorActive) scheduleProactiveRefresh();
      return true;
    }

    if (outcome.kind === "success") {
      // The server has now issued a scoped cookie; the legacy copy is dead weight.
      legacyRefreshToken = null;
      if (isForeignSubject(outcome.accessToken)) {
        // The shared-origin cookie now belongs to somebody else. Do not adopt it.
        if (!skipRedirect) terminateSessionAndRedirect();
        return false;
      }
      applyRefreshSuccess(outcome.accessToken, outcome.expiresIn);
      publishRefreshResult({
        type: "token-refreshed",
        app: "web",
        accessToken: outcome.accessToken,
        expiresIn: outcome.expiresIn,
      });
      if (coordinatorActive) scheduleProactiveRefresh();
      return true;
    }

    if (outcome.kind === "transient") {
      // Server or network is unhappy, the session is not. Keep it and let the
      // proactive timer try again rather than logging the operator out.
      if (coordinatorActive) scheduleRetryAfterTransientFailure();
      return false;
    }

    if (!skipRedirect) {
      terminateSessionAndRedirect();
    }
    return false;
  })().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

/** Re-arm the proactive timer after a transient failure instead of ending the session. */
function scheduleRetryAfterTransientFailure(): void {
  cancelProactiveRefresh();
  if (!coordinatorActive) return;
  proactiveTimerId = setTimeout(() => {
    proactiveTimerId = null;
    void runProactiveRefreshIfNeeded();
  }, PROACTIVE_RETRY_DELAY_MS);
}

function msUntilProactiveRefresh(): number | null {
  const remainingSec = getRemainingAccessSec();
  if (remainingSec == null) return null;
  const delaySec = remainingSec - ACCESS_REFRESH_LEAD_SEC;
  if (delaySec <= 0) return 0;
  return delaySec * 1000;
}

export function cancelProactiveRefresh(): void {
  if (proactiveTimerId != null) {
    clearTimeout(proactiveTimerId);
    proactiveTimerId = null;
  }
}

export function scheduleProactiveRefresh(): void {
  cancelProactiveRefresh();
  if (!coordinatorActive || !getAccessToken()) return;

  const delayMs = msUntilProactiveRefresh();
  if (delayMs == null) return;

  // BE-36: arm the proactive refresh even when the tab is hidden. Gating on
  // `document.hidden` here meant a backgrounded/idle tab never scheduled a
  // refresh timer, so the access token silently expired at its 15m TTL and the
  // next request (or the visibility catch-up) raced into a forced logout —
  // defeating the BE-26 "refresh before expiry" intent and Remember me. Browser
  // background timers still fire (throttled), and the 7-day refresh token keeps a
  // late refresh valid, so the session stays alive while idle.
  proactiveTimerId = setTimeout(() => {
    proactiveTimerId = null;
    void runProactiveRefreshIfNeeded();
  }, delayMs);
}

export function runProactiveRefreshIfNeeded(): void {
  if (!coordinatorActive || !getAccessToken()) return;

  const remaining = getRemainingAccessSec();
  if (remaining == null) return;

  const shouldRefreshNow =
    remaining <= ACCESS_REFRESH_LEAD_SEC ||
    (typeof document !== "undefined" &&
      !document.hidden &&
      remaining < ACCESS_REFRESH_ON_VISIBLE_SEC);

  if (!shouldRefreshNow) {
    scheduleProactiveRefresh();
    return;
  }

  void refreshAccessToken({ proactive: true, skipAutoRedirect: skipAutoRedirectOnFailure });
}

function onVisibilityChange(): void {
  if (typeof document === "undefined") return;
  // BE-36: keep the proactive timer armed while hidden so an idle background tab
  // still refreshes before the access token expires. (Previously, hiding the tab
  // cancelled the timer, so a 15m+ idle stint logged the operator out despite
  // Remember me.) On unhide we still catch up immediately if expiry is near.
  if (document.hidden) return;

  const remaining = getRemainingAccessSec();
  if (remaining != null && remaining < ACCESS_REFRESH_ON_VISIBLE_SEC) {
    void runProactiveRefreshIfNeeded();
    return;
  }
  scheduleProactiveRefresh();
}

function attachVisibilityListener(): void {
  if (visibilityListenerAttached || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", onVisibilityChange);
  visibilityListenerAttached = true;
}

function detachVisibilityListener(): void {
  if (!visibilityListenerAttached || typeof document === "undefined") return;
  document.removeEventListener("visibilitychange", onVisibilityChange);
  visibilityListenerAttached = false;
}

function attachBroadcastListener(): void {
  if (unsubscribeBroadcast) return;
  unsubscribeBroadcast = subscribeRefreshResults("web", (message) => {
    adoptBroadcastToken(message.accessToken, message.expiresIn);
  });
}

function detachBroadcastListener(): void {
  unsubscribeBroadcast?.();
  unsubscribeBroadcast = null;
}

/** Start proactive scheduling, visibility handling and cross-tab token sharing. */
export function startAuthRefreshCoordinator(): void {
  coordinatorActive = true;
  attachVisibilityListener();
  attachBroadcastListener();
  scheduleProactiveRefresh();
}

/** Stop timers, listeners and cross-tab sharing (logout / session terminated). */
export function stopAuthRefreshCoordinator(): void {
  coordinatorActive = false;
  cancelProactiveRefresh();
  detachVisibilityListener();
  detachBroadcastListener();
  clearTokenExpiry();
}

/** Vitest-only: clear in-flight refresh state between cases. */
export function resetRefreshCoordinatorForTests(): void {
  isRefreshing = false;
  refreshPromise = null;
  expectedUserId = null;
  legacyRefreshToken = null;
  detachBroadcastListener();
  cancelProactiveRefresh();
}
